import { defaultShopify } from "@state/store";
import { Shopify } from "@state/types";
import {
  collection,
  doc,
  getFirestore,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { navigate } from "gatsby";
//@ts-expect-error No lib types
import app from "gatsby-plugin-firebase-v9.0";
import { Customer } from "shopify-storefront-api-typings";
import { useStore } from "@state/store";
import { toast } from "react-toastify";
import {
  setSavedRecipes,
  setSavedProducts,
  setSavedBlogs,
  isBrowser,
} from "./helper";

interface FirebaseData {
  savedRecipes: string[];
  savedProducts: string[];
  savedBlogs: string[];
}

export interface FirebaseProps {
  firebase: any;
}
export interface DocumentRefProps {
  refString: string;
  token?: string;
}

export interface FirestoreDataProps {
  email: string | null;
  name: string | null;
  token: string;
}

export const getFirebase = () => {
  if (!isBrowser()) {
    console.log("Not in browser, Firebase unavailable");
    return { app: null, firestore: null };
  }
  
  try {
    console.log("Firebase app:", app);
    const firestore = getFirestore(app);
    console.log("Firestore initialized:", !!firestore);
    return { app, firestore };
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    return { app: null, firestore: null };
  }
};

export async function setDataToFirestore(dataProps: FirestoreDataProps) {
  const { email, name, token } = dataProps;
  const firestore = getFirebase().firestore;
  if (!firestore) return;

  const docRef = doc(collection(firestore, "users"), token);
  const setData = {
    name,
    email,
    token,
  };
  await setDoc(docRef, setData);
  const docSet = await getDoc(docRef);
  if (docSet && docSet.exists()) {
    const data = docSet.data();
    if (data) {
      if (data.savedRecipes) {
        setSavedRecipes(data.savedRecipes);
      }
      if (data.savedProducts) {
        setSavedProducts(data.savedProducts);
      }
      if (data.savedBlogs) {
        setSavedBlogs(data.savedBlogs);
      }
    } else {
      console.log("No such document!");
    }
  }
}

export const updateCustomer = (
  res: {
    token: string;
    customer: Customer;
    cart?: any;
  },
  shopify: Shopify
) => {
  if (!res.customer?.email) {
    customerLogout({ shopify });
    return;
  }
  const email = res.customer?.email;
  const token = res.token;
  const firstName = res.customer?.firstName;
  const cart = res.cart;
  // create user in firestore
  try {
    const firestore = getFirebase().firestore;
    const customerId = res.customer.id.replace("gid://shopify/Customer/", "");
    const firestoreData = {
      firestore,
      email,
      token: customerId,
      name: firstName ?? null,
    };
    setDataToFirestore(firestoreData);
  } catch (err) {
    console.log(err);
  }
  let newState: Shopify = shopify;
  newState.customer = {
    ...res.customer,
    token: token,
  };
  if (cart) {
    newState.cart = cart;
  }
  useStore.setState({ shopify: newState });
};

export function customerLogout({ shopify }: { shopify: Shopify }) {
  navigate("/");
  const newState = {
    shopify: {
      ...shopify,
      customer: {
        ...defaultShopify.customer,
      },
    },
    savedRecipes: [],
    savedProducts: [],
    savedBlogs: [],
  };
  useStore.setState(newState);
}

export const handleSaveRecipeProduct = (
  id: Queries.Maybe<string> | undefined,
  type: "product" | "recipe" | "blog"
) => {
  console.log("handleSaveRecipeProduct called", { id, type });
  
  const token = useStore.getState().getCustomerId();
  const visibility = useStore.getState().visibility;
  console.log("token", token);

  if (!token) {
    console.log("No token found, showing login drawer");
    useStore.setState({
      visibility: { ...visibility, loginDrawerVisible: true },
    });
    return;
  }

  if (!id) {
    console.log("No id provided");
    return;
  }

  const firebase = getFirebase();
  console.log("Firebase instance:", firebase);
  const firestore = firebase.firestore;
  if (!firestore) {
    console.error("Firestore is not initialized!");
    toast.error("Unable to save favorite. Please refresh the page and try again.");
    return;
  }

  const defaultSavedProducts = useStore.getState().savedProducts;
  const defaultSavedRecipes = useStore.getState().savedRecipes;
  const defaultSavedBlogs = useStore.getState().savedBlogs;
  const savedObj =
    type === "recipe"
      ? defaultSavedRecipes
      : type === "product"
      ? defaultSavedProducts
      : type === "blog"
      ? defaultSavedBlogs
      : [];
  const uniqueIds =
    savedObj && savedObj.length > 0 ? [...new Set([...savedObj, id])] : [id];
  const savedIds =
    savedObj && savedObj.includes(id)
      ? savedObj.filter(obj => obj !== id)
      : uniqueIds;

  const removeId = savedObj.filter(obj => obj !== id);

  let savedData: FirebaseData = {
    savedRecipes: type === "recipe" ? savedIds : defaultSavedRecipes,
    savedProducts: type === "product" ? savedIds : defaultSavedProducts,
    savedBlogs: type === "blog" ? savedIds : defaultSavedBlogs,
  };
  let errorData: FirebaseData = {
    savedRecipes: type === "recipe" ? removeId : defaultSavedRecipes,
    savedProducts: type === "product" ? removeId : defaultSavedProducts,
    savedBlogs: type === "blog" ? removeId : defaultSavedBlogs,
  };

  if (type === "recipe") {
    setSavedRecipes(savedIds);
  }
  if (type === "product") {
    setSavedProducts(savedIds);
  }
  if (type === "blog") {
    setSavedBlogs(savedIds);
  }
  const typeCapitalized = type.charAt(0).toUpperCase() + type.slice(1);

  console.log("savedData", token, savedData);
  console.log("Attempting to save to Firestore...");

  try {
    const savedDataDoc = doc(collection(firestore, "users"), token);
    console.log("Document reference created:", savedDataDoc.path);
    
    setDoc(savedDataDoc, savedData)
      .then(() => {
        console.log("Successfully saved to Firestore!");
        toast.success(
          savedObj.includes(id)
            ? `${typeCapitalized} removed from your Favourites`
            : `${typeCapitalized} added to your Favourites`
        );
      })
      .catch((error: any) => {
        console.error("Firestore save error:", error);
        useStore.setState(errorData);
        toast.warn(`Error saving ${type}: ${error.message || error}`);
      });
  } catch (error: any) {
    console.error("Error creating Firestore document:", error);
    toast.error(`Failed to save favorite: ${error.message || error}`);
  }
};
